// @flow
import * as React from 'react';
import styled from 'styled-components';
import Countdown from 'react-countdown-now';
import colors from '../../assets/css/colors';
import { below } from "../../assets/css/media";

const CountdownTimerWrapper = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
`;

const Unit = styled.div`
	font-size: 17px;
	font-weight: bold;
	letter-spacing: 0.5px;
	line-height: 1;
	text-align: center;
	color: ${colors.nrlDarkGreen};
`;

const Label = styled.div`
	color: ${colors.darkGrey};
	font-size: 8px;
	line-height: 2;
	text-align: center;
	margin-top: .25em;
	text-transform: ${({ is_uppercase }) => is_uppercase ? 'uppercase' : 'none'};
	
	${below.tablet`
		background: #ffffff;
		margin-top: 0;
	`}
`;

const CountdownTimerCell = styled.div`
	margin: 0 .5em;
	
	${below.tablet`
		margin: 1px;
	`}
`;

const checkForMultiplyDateTimeLabel = unit => parseInt(unit, 10) === 1 ? '' : 's';

type Props = {
	date: string,
	onComplete?: Function,
	label_type?: string,
	is_uppercase?: boolean,
	className?: string,
	hideHoursIfZero?: boolean,
	showOnComplete?: boolean,
}

const renderer = (
	{
		days,
		hours,
		minutes,
		seconds,
		completed,
		label_type,
		is_uppercase,
		hideHoursIfZero,
		showOnComplete,
		...props
	}
) => {
	if([!showOnComplete, completed].every(flag => flag)) {
		// Render a completed state
		return null;
	}

	const label = {
		full: {
			days: 'Day',
			hours: 'Hour',
			minutes: 'Minute',
			seconds: 'Second'
		},
		short: {
			days: 'Day',
			hours: 'Hr',
			minutes: 'Min',
			seconds: 'Sec'
		}
	}[label_type || 'short'];

	// Render a countdown
	return (
		<CountdownTimerWrapper className={props.className}>
			{days ? (
				<CountdownTimerCell>
					<Unit className={'timer-unit'}>
						{days}
					</Unit>
					<Label is_uppercase={is_uppercase} className={'timer-label'}>
						{label.days}{checkForMultiplyDateTimeLabel(days)}
					</Label>
				</CountdownTimerCell>
			) : null}
			{ hideHoursIfZero && hours === '00' ? null : (
				<CountdownTimerCell>
					<Unit className={'timer-unit'}>
						{hours}
					</Unit>
					<Label is_uppercase={is_uppercase} className={'timer-label'}>
						{label.hours}{checkForMultiplyDateTimeLabel(hours)}
					</Label>
				</CountdownTimerCell>
			) }
			<CountdownTimerCell>
				<Unit className={'timer-unit'}>
					{minutes}
				</Unit>
				<Label is_uppercase={is_uppercase} className={'timer-label'}>
					{label.minutes}{checkForMultiplyDateTimeLabel(minutes)}
				</Label>
			</CountdownTimerCell>
			{!days ? (
				<CountdownTimerCell>
					<Unit className={'timer-unit'}>
						{seconds}
					</Unit>
					<Label is_uppercase={is_uppercase} className={'timer-label'}>
						{label.seconds}{checkForMultiplyDateTimeLabel(seconds)}
					</Label>
				</CountdownTimerCell>
			) : null}
		</CountdownTimerWrapper>
	);
};

class InstantCountdown extends Countdown {
	setDeltaState(delta) {
		if (!this.state.completed && delta.completed) {
			if (this.props.onComplete) {
				this.clearInterval();
				this.props.onComplete(delta);
			}
		}

		if (this.mounted) {
			this.setState({ ...delta });
		}
	}
}

export const CountdownTimer = ({
	date,
	onComplete,
	label_type,
	is_uppercase,
	className,
	hideHoursIfZero,
	showOnComplete,
}: Props) => {
	const to_date = new Date(date);

	return !isNaN(to_date.getTime()) ? (
		<InstantCountdown
			date={to_date}
			onComplete={onComplete}
			renderer={({ ...props }) => renderer({
				label_type, is_uppercase, hideHoursIfZero, className, showOnComplete, ...props
			})}
		/>
	) : null;
};

export default CountdownTimer;


// WEBPACK FOOTER //
// ./src/components/CountdownTimer/index.js